import {
	Autocomplete,
	TextField,
	AutocompleteProps,
	ChipTypeMap,
	Chip,
	AutocompleteRenderInputParams,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { color, fontWeight, lineHeight } from '@mui/system'

type Value = AutocompleteProps<any, any, any, any>['value']
type Multiple = boolean | undefined
type DisableClearable = boolean | undefined
type FreeSolo = true | undefined
type ChipComponent = typeof Chip
interface CustomComboBoxProps
	extends Omit<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>, 'renderInput'> {
	label: string
	placeholder?: string
	renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
}

// Extra styling for the CustomComboBox component and its subcomponents
const comboBoxPopupIconSx = (disabled: boolean | undefined) => ({
	color: disabled ? 'var(--primary-light)' : 'var(--primary)',
	fontFamily: 'Poppins',
})

const comboBoxInputLabelSx = {
	fontFamily: 'var(--font-family)',
	fontSize: '0.75rem',
	color: 'var(--primary-dark)',
	fontWeight: 500,
	lineHeight: 1.5,
}

const comboBoxInputPropsSx = {
	fontFamily: 'var(--font-family)',
	fontSize: '13px',
	color: 'var(--primary-dark)',
	fontWeight: 500,
	lineHeight: 1.3,
	'& fieldset': {
		borderColor: 'rgba(134, 149, 175, 0.30)',
	},
	'&.Mui-focused fieldset': {
		borderColor: 'var(--primary) !important',
	},
}

const comboBoxSx = {
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'row',
}

const comboBoxPaperSx = {
	'& .MuiAutocomplete-groupLabel': {
		fontFamily: 'Poppins',
		color: 'var(--primary-dark)',
		fontSize: '12px',
		fontWeight: 500,
		lineHeight: 1.5,
	},
	'& .MuiAutocomplete-option': {
		fontFamily: 'Poppins',
		color: 'var(--primary-dark)',
		fontSize: '12px',
		fontWeight: 500,
		lineHeight: 1.5,
	},
}

/**
 * A customizable combo box component for selecting any item.
 *
 * It implementes **_MUI's Autocomplete_** component with custom styling. The styling can be further customized via the `sx` prop, or providing a new JSX element through the `renderInput` prop, as well as customizing the popup icon via the `popupIcon` prop.
 *
 * It differs from the standard **_Autocomplete_** by providing default popup icon and input field styling.
 *
 * For more advanced use cases and implementation details, refer to [MUI's Autocomplete documentation](https://mui.com/material-ui/react-autocomplete/).
 *
 * - **@param {_CustomComboBoxProps_} `props`**: Props for configuring the CustomComboBox component. The same as MUI Autocomplete props with additional:
 *
 *   - **@param {_string_} `label`**: Label for the combo box input field
 *
 *   - **@param {_string_} `placeholder`**: Placeholder text for the combo box input field
 *
 * - **@returns {_JSX.Element_}**: The rendered CustomComboBox component
 */
function CustomComboBox<
	Value,
	Multiple extends boolean | undefined = false,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean | undefined = false,
	ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>(props: CustomComboBoxProps): JSX.Element {
	return (
		<Autocomplete
			{...props}
			popupIcon={props.popupIcon ?? <ExpandMoreIcon sx={comboBoxPopupIconSx(props.disabled)} />}
			renderInput={(params: AutocompleteRenderInputParams) => {
				if (props.renderInput) {
					return props.renderInput(params)
				}
				return (
					<TextField
						{...params}
						label={props.label}
						placeholder={props.placeholder}
						sx={{
							'& .MuiInputLabel-root': comboBoxInputLabelSx,
						}}
						InputProps={{
							...params.InputProps,
							sx: comboBoxInputPropsSx,
						}}
					/>
				)
			}}
			sx={{
				comboBoxSx,
				...props.sx,
			}}
			// Custom styles for the dropdown paper and options
			componentsProps={{
				paper: {
					sx: { ...comboBoxPaperSx },
				},
				...props.componentsProps,
			}}
		/>
	)
}

export default CustomComboBox
